/**
 * Wave 78 F1 · SUB 2 — CronogramaVivo.
 *
 * Substitui `Timeline.tsx` (que caía em fallback hardcoded 7 etapas vazias
 * "Fundação — — / Estrutura — — / ...") por uma timeline real puxada de
 * `work_stages`. O backend (showcase.service.ts SUB 2) faz o fallback
 * silencioso de `showcase_milestones` → `work_stages` quando os primeiros
 * estão vazios, então este componente sempre recebe ou (a) milestones
 * reais, (b) milestones derivados de work_stages, ou (c) array vazio
 * (projeto sem cronograma cadastrado).
 *
 * Princípio (PRD §5.2):
 *   • Marcadores auto-puxados de work_stages
 *   • Status por progress_percent:
 *       0       → marker vazio (futuro)
 *       0 < x < 100 → marker barra parcial accent (em andamento)
 *       100     → marker preenchido sólido (concluído)
 *   • Último estágio (is_delivery) ganha pulsing ring
 *   • Datas: "Concluído MAI/2026" (actual_end) ou "Previsto JUL/2026"
 *     (planned_end) ou "—" (nada cadastrado)
 *
 * Layout: rail horizontal scrollytelling (mesma metáfora do Timeline
 * anterior). Os 300vh ficam porque essa cena tem ritmo lento por
 * design — buyer absorve o caminho da obra.
 *
 * Empty state: NÃO mostra 7 etapas hardcoded como antes. Mostra mensagem
 * "O cronograma será publicado quando a obra começar" — honestidade
 * editorial. Melhor mostrar nada do que mentir.
 */

import { useMemo, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import type { Milestone } from '../lib/api';
import { monthLabel } from '../lib/api';

interface Props {
  milestones: Milestone[];
}

export function CronogramaVivo({ milestones }: Props) {
  const items = useMemo(() => milestones, [milestones]);

  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });

  // Horizontal sweep — last 15% reserved for fade so nothing feels abrupt.
  const x = useTransform(scrollYProgress, [0, 0.85], ['5%', '-90%']);

  // Empty state: short section (não 300vh), single column, honest copy.
  if (items.length === 0) {
    return (
      <section
        className="relative border-t border-line bg-bg py-24 sm:py-32"
        aria-label="Cronograma"
      >
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            Linha do tempo
          </p>
          <h2 className="mt-5 font-display text-3xl font-light leading-[1.1] tracking-tighter text-ink sm:text-4xl">
            Cronograma em preparação.
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-ink-muted sm:text-base">
            Será publicado assim que a obra começar — cada etapa com data
            prevista, andamento real e marcos auditáveis.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={ref}
      className="relative h-[300vh] border-t border-line bg-bg"
      aria-label="Cronograma"
    >
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto w-full max-w-6xl px-6 sm:px-12 lg:px-20">
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            Linha do tempo
          </p>
          <h2 className="mt-5 max-w-[18ch] font-display text-4xl font-light leading-[1.05] tracking-tighter text-ink sm:text-5xl">
            Do primeiro furo até a chave.
          </h2>
        </div>

        <div className="relative mt-20 flex-1">
          {/* Fade-out masks on the edges so milestones flow out naturally */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-bg to-transparent"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-bg to-transparent"
          />

          <motion.ol
            className="flex items-center gap-24 whitespace-nowrap will-change-transform sm:gap-40"
            style={{ x }}
          >
            {items.map((m, i) => (
              <MilestoneCard
                key={`${m.label}-${i}`}
                milestone={m}
                index={i}
                progressPercent={inferProgressPercent(m)}
              />
            ))}
          </motion.ol>

          {/* Continuous rail behind the markers */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line"
          />
        </div>
      </div>
    </section>
  );
}

/**
 * Front-end mirrors the back-end mapping: completedAt → 100, no completedAt
 * but estimatedAt → 0 (or partial if we want), nothing → 0.
 *
 * For F1 we treat 100 vs 0 only — `in-progress` requires explicit
 * progress_percent which isn't yet wired through the Milestone DTO. F2
 * widens the contract; F1 keeps the contract additive (no breaking change
 * to the Milestone type).
 */
function inferProgressPercent(m: Milestone): number {
  if (m.completedAt) return 100;
  return 0;
}

function MilestoneCard({
  milestone,
  index,
  progressPercent,
}: {
  milestone: Milestone;
  index: number;
  progressPercent: number;
}) {
  const completed = progressPercent >= 100;
  const inProgress = progressPercent > 0 && progressPercent < 100;
  const isDelivery = milestone.isDelivery;
  const alignAbove = index % 2 === 0;
  const dateLabel = formatDateLabel(milestone);

  return (
    <li
      className="relative flex min-w-[220px] flex-col items-center"
      aria-label={`${milestone.label} — ${dateLabel}`}
    >
      {alignAbove && (
        <div className="mb-12 w-[220px] text-center">
          <p className="font-display text-xl font-light tracking-tight text-ink">
            {milestone.label}
          </p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-ink-subtle">
            {dateLabel}
          </p>
        </div>
      )}

      <div className="relative">
        <span
          className={[
            'block rounded-full transition-colors duration-300',
            isDelivery
              ? 'h-5 w-5 border-[1.5px] border-accent bg-bg shadow-[0_0_0_4px_rgba(212,184,150,0.18)]'
              : completed
                ? 'h-3.5 w-3.5 bg-ink'
                : inProgress
                  ? 'h-4 w-4 border-[1.5px] border-accent bg-accent/40'
                  : 'h-3 w-3 border-[1.5px] border-line bg-bg',
          ].join(' ')}
          style={
            isDelivery
              ? { animation: 'cronoPulse 1.6s cubic-bezier(0.4, 0, 0.6, 1) infinite' }
              : undefined
          }
        />
      </div>

      {!alignAbove && (
        <div className="mt-12 w-[220px] text-center">
          <p className="font-display text-xl font-light tracking-tight text-ink">
            {milestone.label}
          </p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-ink-subtle">
            {dateLabel}
          </p>
        </div>
      )}

      <style>{`
        @keyframes cronoPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 4px rgba(212,184,150,0.18); }
          50% { transform: scale(1.15); box-shadow: 0 0 0 8px rgba(212,184,150,0.08); }
        }
      `}</style>
    </li>
  );
}

function formatDateLabel(m: Milestone): string {
  if (m.completedAt) return `Concluído · ${formatShort(m.completedAt)}`;
  if (m.estimatedAt) return `Previsto · ${formatShort(m.estimatedAt)}`;
  return '—';
}

function formatShort(isoDate: string): string {
  // Accept YYYY-MM-DD or YYYY-MM. monthLabel returns lower-case month
  // names; we upper-case the first letter for caps-driven typography.
  const ym = isoDate.length >= 7 ? isoDate.slice(0, 7) : isoDate;
  const label = monthLabel(ym);
  if (!label) return isoDate;
  // "abril 2026" → "ABR/2026" feels more like a programme schedule.
  const [month, year] = label.split(' ');
  if (!month || !year) return label;
  return `${month.slice(0, 3).toUpperCase()}/${year}`;
}
